/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventShareLink
 * --------------
 * The event's public details-page link, read-only, with a Copy button.
 *
 * Renders nothing for a DRAFT event: the details page does not open for a
 * draft, so a link to it would only lead visitors to a dead end. `status` is
 * the SAVED status, never the one picked in the form but not yet saved.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { EVENTS_PATH } from '../../../pages/feed/feedRoutes';

export const eventShareUrl = (eventId) => (
  `${window.location.origin}${EVENTS_PATH}/${encodeURIComponent(eventId)}`
);

/** A draft has no link to share: its page does not open for visitors. */
export const hasShareLink = (eventId, status) => (
  eventId != null && eventId !== '' && !!status && status !== 'DRAFT'
);

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // No clipboard API (an http origin, an older browser): copy through a
    // throwaway selection instead.
    const input = document.createElement('textarea');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }
}

export default function EventShareLink({ eventId, status, visibility, label = 'Event link' }) {
  const id = useId();
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!hasShareLink(eventId, status)) return null;

  const url = eventShareUrl(eventId);

  const copy = async () => {
    await copyText(url);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="orgf-field orgf-share">
      <label id={id}>{label}</label>
      <div className="orgf-share-row">
        {/* Not a text box: the link is generated, never typed, so it reads as a
            value and a click on it copies, the same as the button beside it. */}
        <button
          type="button"
          className="orgf-input orgf-share-url"
          aria-labelledby={id}
          title="Click to copy"
          onClick={copy}
        >
          {url}
        </button>
        <button type="button" className="org-btn ghost" onClick={copy}>
          <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`} aria-hidden="true" />
          {' '}{copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <span className="orgf-help" aria-live="polite">
        {copied ? 'Link copied to the clipboard.' : (visibility === 'PRIVATE'
          ? 'This event is private. The link works only for you.'
          : 'Anyone with this link can open the event page.')}
      </span>
    </div>
  );
}

/**
 * The same link as an icon button, for a row in the organizer's event list.
 * Sits inside a row that opens the event on click and on Enter/Space, so its
 * own click and keys stop there — copying must not also open the form.
 */
export function EventCopyLinkButton({ eventId, status }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!hasShareLink(eventId, status)) return null;

  // Not the title: the row is found and announced by its title, and a second
  // control carrying the same words would make it ambiguous.
  const label = copied ? 'Link copied' : 'Copy event link';

  const copy = async (event) => {
    event.stopPropagation();
    await copyText(eventShareUrl(eventId));
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      className={`orge-copy${copied ? ' is-copied' : ''}`}
      onClick={copy}
      onKeyDown={(event) => event.stopPropagation()}
      aria-label={label}
      title={copied ? 'Link copied' : 'Copy event link'}
    >
      <i className={`bi ${copied ? 'bi-clipboard-check' : 'bi-clipboard'}`} aria-hidden="true" />
      {/* Decorative: the button's own label already says "Link copied". */}
      {copied && <span className="orge-copy-chip" aria-hidden="true">Copied</span>}
    </button>
  );
}
